import React from "react";
import PropTypes from "prop-types";
import Icono from "./CTIcons";
import CTPopupMenu from "./CTPopupMenu";
import { useFormContext, Controller } from "react-hook-form";

const ButtonContent = ({
  icon: Icon,
  iconIndex,
  iconPosition,
  iconSize,
  iconColor,
  children,
}) => {
  const renderIcon = () => {
    if (iconIndex)
      return (
        <Icono
          name={iconIndex}
          size={iconSize}
          color={iconColor}
          className="inline-flex"
        />
      );
    if (Icon)
      return <Icon size={iconSize} color={iconColor} className="inline-flex" />;
    return null;
  };

  const isVertical = iconPosition === "top" || iconPosition === "bottom";
  const flexClass = isVertical ? "btn-flex-col" : "btn-flex-row";

  return (
    <div className={`${flexClass}`}>
      {(iconPosition === "top" || iconPosition === "left") && renderIcon()}
      {children && <span>{children}</span>}
      {(iconPosition === "bottom" || iconPosition === "right") && renderIcon()}
    </div>
  );
};

const CTButton = ({
  icon,
  iconIndex,
  children,
  onClick,
  disabled = false,
  visible = true,
  className = "",
  title = "",
  iconPosition = "left",
  variant = "primary",
  type = "button",
  iconSize = "1em",
  iconColor = "currentColor",
  name,
  tag = 0,
  height,
  align = "center",
  fontSize, // 🔹 nuevo prop tailwind
  bold = false, // 🔹 nuevo prop tailwind
  focusRing = true, // 🔹 nuevo
  popupmenu = null, // 🔹 nuevo prop: array de opciones
}) => {
  const formContext = useFormContext();
  const control = formContext?.control;

  // const variantClass = `btn-${variant || "primary"}`;

  const variantClass = {
    primary: "btn-sap-primary",
    secondary: "btn-sap-secondary",
    emphasized: "btn-sap-emphasized",
    transparent: "btn-sap-transparent",
    ghost: "btn-sap-ghost",
  }[variant || "primary"];
  const disabledClass = disabled ? "btn-disabled" : "";

  const style = {};
  if (height) style.height = height;

  let alignClass = "";
  if (align === "left") alignClass = "justify-start";
  else if (align === "right") alignClass = "justify-end";
  else alignClass = "justify-center";

  // Tailwind font size y bold
  const fontClass = fontSize ? fontSize : "text-base";
  const boldClass = bold ? "font-bold" : "font-normal";
  const focusClass = !focusRing ? "focus:outline-none focus:ring-0" : "";
  const buttonClasses = `btn-base ${variantClass} ${disabledClass} ${alignClass} ${fontClass} ${boldClass} ${focusClass} ${className}`;

  const computedTitle = title || (typeof children === "string" ? children : "");

  const [menuOpen, setMenuOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState({ x: 0, y: 0 });
  const buttonRef = React.useRef(null);

  const handleClick = (e) => {
    if (popupmenu && popupmenu.length) {
      // calcular posición del botón
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ x: rect.left, y: rect.bottom });
      setMenuOpen(true);
    }
    onClick?.(e); // también ejecuta el onClick normal
  };


  if (!visible) return null;

 // render del botón
  const button = (
    <button
      type={type}
      ref={buttonRef}
      className={buttonClasses}
      style={style}
      onClick={handleClick}
      disabled={disabled}
      data-tag={tag}
      title={computedTitle}
      aria-label={computedTitle}
    >
      <ButtonContent
        icon={icon}
        iconIndex={iconIndex}
        iconPosition={iconPosition}
        iconSize={iconSize}
        iconColor={iconColor}
      >
        {children}
      </ButtonContent>
    </button>
  );

  return (
    <>
      {(!name || !control) && button}
      {name && control && <Controller control={control} name={name} render={() => button} />}

      {/* 🔹 Renderizar popup menu si existe */}
      {popupmenu && popupmenu.length > 0 && menuOpen && (
        <CTPopupMenu
          x={menuPos.x}
          y={menuPos.y}
          options={popupmenu}
          onSelect={(option, e) => {
            option.onSelect?.(option, e); // opción específica
            setMenuOpen(false);
          }}
          onClose={() => setMenuOpen(false)}
          parentWidth={buttonRef.current?.offsetWidth || 0}
        />
      )}
    </>
  );
};



CTButton.propTypes = {
  icon: PropTypes.elementType,
  iconIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  iconPosition: PropTypes.oneOf(["left", "right", "top", "bottom"]),
  children: PropTypes.node,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  visible: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string,
  name: PropTypes.string,
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "transparent",
    "emphasized",
    "ghost",
  ]),
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  iconSize: PropTypes.number,
  iconColor: PropTypes.string,
  tag: PropTypes.number,
  height: PropTypes.string,
  align: PropTypes.oneOf(["left", "center", "right"]),
  fontSize: PropTypes.string, // tailwind text-sm, text-lg, text-xl...
  bold: PropTypes.bool,
  focusRing: PropTypes.bool,
};

export default CTButton;
